import { NavLink } from 'react-router'
import {
  LayoutDashboard,
  Users,
  FileText,
  Calendar,
  Stethoscope,
  BarChart3,
  Settings,
  LogOut,
  Receipt,
  UserCog,
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { useSidebarStore } from '../store/sidebarStore'
import { useLanguage } from '../hooks/useLanguage'
import { t } from '../utils/translations'

const navigationItems: Array<{
  key: string
  href: string
  icon: typeof LayoutDashboard
  requiresAdmin?: boolean
}> = [
  { key: 'dashboard', href: '/dashboard', icon: LayoutDashboard },
  { key: 'calendario', href: '/calendario', icon: Calendar },
  { key: 'pacientes', href: '/pacientes', icon: Users },
  { key: 'atenciones', href: '/atenciones', icon: FileText },
  { key: 'gastos', href: '/gastos', icon: Receipt },
  { key: 'reportes', href: '/reportes', icon: BarChart3 },
  { key: 'tratamientos', href: '/tratamientos', icon: Stethoscope },
  { key: 'usuarios', href: '/usuarios', icon: UserCog, requiresAdmin: true },
  { key: 'configuracion', href: '/configuracion', icon: Settings },
]

export default function Sidebar() {
  const { logout, user } = useAuthStore()
  const { isCollapsed, toggleSidebar } = useSidebarStore()
  const language = useLanguage()

  const visibleItems = navigationItems.filter(
    (item) => !item.requiresAdmin || user?.rol === 'ADMIN'
  )

  return (
    <aside
      className={`hidden md:flex md:flex-col shrink-0 h-full min-h-0 overflow-hidden transition-all duration-300 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 ${
        isCollapsed ? 'md:w-16' : 'md:w-64'
      }`}
    >
      {/* Logo — fijo arriba */}
      <div
        className="shrink-0 pt-5 pb-4 px-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        onClick={toggleSidebar}
        title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
      >
        <div className="flex items-center w-full">
          <div className="shrink-0">
            <div className="w-8 h-8 bg-white dark:bg-gray-700 rounded-lg flex items-center justify-center">
              <img
                src="https://tatianaldaz.com/wp-content/uploads/2019/08/TatianaAldaz_Logo.png"
                alt="Kenzen Logo"
                className="w-full h-full object-contain rounded-lg"
              />
            </div>
          </div>
          {!isCollapsed && (
            <div className="ml-3 min-w-0">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">Kenzen</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">Facturación</p>
            </div>
          )}
        </div>
      </div>

      {/* Navegación — única zona con scroll */}
      <nav
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 py-2 space-y-1"
        aria-label="Menú principal"
      >
        {visibleItems.map((item) => {
          const Icon = item.icon
          const itemName = t(item.key, language)
          return (
            <NavLink
              key={item.key}
              to={item.href}
              className={({ isActive }) =>
                `group flex items-center text-sm font-medium rounded-md transition-colors ${
                  isCollapsed ? 'justify-center px-2 py-3' : 'px-2 py-2'
                } ${
                  isActive
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-200'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                }`
              }
              title={isCollapsed ? itemName : undefined}
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={`shrink-0 h-5 w-5 ${
                      isCollapsed ? '' : 'mr-3'
                    } ${
                      isActive
                        ? 'text-primary-500'
                        : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  />
                  {!isCollapsed && <span className="truncate">{itemName}</span>}
                </>
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* Usuario — fijo abajo */}
      <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 p-4">
        {isCollapsed ? (
          <div className="flex flex-col items-center">
            <button
              type="button"
              onClick={logout}
              className="p-2 rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={t('cerrar-sesion', language)}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center min-w-0">
            <div className="shrink-0">
              <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center overflow-hidden">
                {user?.foto_perfil ? (
                  <img
                    src={user.foto_perfil}
                    alt={user?.nombre || user?.username || 'Usuario'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {(user?.nombre?.charAt(0) || user?.username?.charAt(0) || 'U').toUpperCase()}
                  </span>
                )}
              </div>
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                {user?.nombre && user?.apellidos
                  ? `${user.nombre} ${user.apellidos}`
                  : user?.nombre || user?.username}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
            </div>
            <button
              type="button"
              onClick={logout}
              className="ml-2 shrink-0 p-1 rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={t('cerrar-sesion', language)}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}
